import { useContext, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { MeContext } from 'contexts/me'
import Skeleton from 'react-loading-skeleton'

// utils
import WORDING from 'constants/wording'
import { redirectWhatsApp } from 'utils/redirectWhatsApp'
import { SOCIAL_LINKS, ZENDESK_URL } from 'config'
import { hasActiveMembership, hasSuspendedMembership } from 'utils'

// Custom components}
import Card from 'components/Card'
import PageWrapper from 'components/PageWrapper'
import PrimaryButton from 'components/primaryButton'
import { MembershipTag } from 'components/Tag'
import RoundedIcon from 'components/RoundedIcon'
import { SuspendedMembershipModal } from 'components/MembershipSubscriptionModal'

// Assets
import { Facebook, Twitter, Instagram } from 'assets/icons'
import advisor from 'assets/images/advisor.png'
import whatsapp from 'assets/images/whatsapp.png'
import { trackEvent } from 'analytics/trackFunctions'

const ContactUs = () => (
  <PageWrapper
    viewSelected='/centro-de-ayuda'
    className='!px-0 md:px-4'
    title='Contactanos'
    noTabBar
  >
    <section className='px-4 mt-4 text-black md:flex md:max-w-564 md:flex-col mb-20 md:mb-5 mx-auto'>
      <div className='space-y-4'>
        <AdvisorCard />
        <p className='text-xs'>Comunicate con nosotros por redes sociales</p>
        <div className='gap-4 grid grid-cols-3'>
          <SocialButton
            label='Instagram'
            icon={<Instagram />}
            url={SOCIAL_LINKS.ig}
            event='bille_contacto_boton_ig'
          />
          <SocialButton
            label='Facebook'
            icon={<Facebook />}
            url={SOCIAL_LINKS.fb}
            event='bille_contacto_boton_fb'
          />
          <SocialButton
            label='Twitter'
            icon={<Twitter />}
            url={SOCIAL_LINKS.x}
            event='bille_contacto_boton_tw'
          />
        </div>
        <p className='text-xs'>
          También podés enviarnos tu consulta por correo
        </p>
        <PrimaryButton
          outlined
          buttonText='Enviar un correo'
          primaryButtonFunction={() => {
            trackEvent({ eventName: 'bille_contacto_boton_mail' })
            window.open(`${ZENDESK_URL}/requests/new`, '_blank')
          }}
        />
      </div>
    </section>
  </PageWrapper>
)

export default ContactUs

const SocialButton = ({ label, icon, url, event }) => (
  <button
    onClick={() => {
      trackEvent({ eventName: event })
      window.open(url, '_blank')
    }}
    type='button'
    className='w-full bg-white border border-gray-500 rounded-lg py-4 flex items-center justify-center'
  >
    <span className='sr-only'>{label}</span>
    {icon}
  </button>
)

const AdvisorCard = ({ className = '' }) => {
  const [isOpen, setIsOpen] = useState(false)
  const { me } = useContext(MeContext)
  const navigate = useNavigate()

  useEffect(() => {
    trackEvent({ eventName: 'bille_pantalla_contacto' })
  }, [])

  const hasMembership =
    hasActiveMembership(me?.subscriber_products) ||
    hasSuspendedMembership(me?.subscriber_products)

  if (!me) return <Skeleton height={160} />

  return (
    <>
      <Card
        className={`w-full py-4 px-3 rounded-lg mt-4 relative !outline-none shadow-card-benefits items-center flex justify-between gap-4 ${
          hasMembership ? 'bg-purple-900 text-white' : 'bg-white text-black'
        } ${className}`}
      >
        <MembershipTag
          className='absolute -top-[10px] left-0 !py-0'
          text='Beneficio Wayni+'
        />
        <div>
          <p className='font-bold text-xl'>Hablá con tu asesor</p>
          <p className='text-xs font-normal'>
            Resolvé rapidamente tus consultas a través de nuestro chat en
            Whatsapp.
          </p>
          {hasMembership ? (
            <button
              onClick={() => {
                trackEvent({ eventName: 'bille_contacto_banner_wpp_suscripto' })
                hasSuspendedMembership(me?.subscriber_products)
                  ? setIsOpen(true)
                  : redirectWhatsApp(me?.profile?.name)
              }}
              className='mt-3 border border-white px-6 py-2 rounded-[1.75rem] font-bold text-xs'
            >
              Iniciar conversación
            </button>
          ) : (
            <button
              onClick={() => {
                trackEvent({
                  eventName: 'bille_contacto_banner_wpp_no_suscripto'
                })
                navigate('/suscripcion')
              }}
              className='mt-3 bg-purple-900 text-white px-6 py-2 rounded-[1.75rem] font-bold text-xs'
            >
              Suscribirse a Wayni<span className='text-green-800'>+</span>
            </button>
          )}
        </div>
        <div className='shrink-0 w-24 relative'>
          <RoundedIcon
            className='bg-white !h-11 !w-11 absolute -bottom-2 right-0'
            icon={<img src={whatsapp} alt='' />}
          />
          <img className='' src={advisor} alt='' />
        </div>
      </Card>
      <SuspendedMembershipModal
        description={WORDING.SUSPENDED_MEMBERSHIP_HELP_CENTER}
        isModalOpen={isOpen}
        setIsModalOpen={setIsOpen}
      />
    </>
  )
}
